/**
 * #39 · Retiro de lote del mercado. Es la acción de mayor impacto del
 * dashboard y la única del producto que es irreversible desde la UI.
 *
 * "Recall en un click" — qué significa y qué no
 * --------------------------------------------
 * El criterio de aceptación es *retiro en un click*. Un click quiere decir que
 * desde la fila del lote se llega a retirarlo **sin navegar a otra pantalla**:
 * el analista que está mirando la lista no tiene que buscar dónde está la
 * función. No quiere decir que un click suelto lo dispare. Un retiro:
 *
 *   · saca producto del mercado y le muestra al consumidor una alerta
 *     sanitaria explícita — la única excepción a la regla 4 del dominio;
 *   · hace que todo escaneo posterior de ese lote genere alerta crítica;
 *   · no tiene endpoint de deshacer. La API no expone "des-retirar" y no
 *     debería: un aviso sanitario que aparece y desaparece es peor que
 *     cualquiera de los dos estados estables.
 *
 * De ahí los dos pasos, que son el otro criterio del issue:
 *
 *   paso 1  ALCANCE — qué producto, qué lote, qué territorio, cuántas
 *           unidades activadas están afectadas — y el motivo, obligatorio.
 *   paso 2  CONFIRMACIÓN — hay que escribir el código de lote exacto. Es la
 *           misma defensa que usa GitHub para borrar un repo, y sirve para lo
 *           mismo: hace imposible retirar el lote de al lado por inercia.
 *
 * El motivo no es decorativo: queda en el log estructurado del servidor con su
 * `trace_id`, su actor y su timestamp (`services/api/routers/batches.py`).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { batchKeys } from '@/api/keys';
import type { Schemas } from '@/api/types';
import { useApi } from '@/api/useApi';
import { useI18n } from '@/i18n/useI18n';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { ErrorState } from '@/ui/states';
import { Dialog } from '../products/ui/Dialog';
import { DetailList, DetailRow, TextAreaField, TextField } from '../products/ui/fields';

type Batch = Schemas['BatchOut'];

/** Mínimo del motivo. Corto para no estorbar, suficiente para que diga algo. */
const MIN_REASON = 10;

type Step = 'scope' | 'confirm' | 'done';

export interface RecallDialogProps {
  batch: Batch;
  onClose: () => void;
}

/**
 * **Se monta sólo cuando hay un retiro en curso y se desmonta al cerrar.** No
 * recibe `open`: el que decide es quien lo renderiza.
 *
 * Es deliberado y es lo que hace que el segundo paso siga sirviendo. Con el
 * diálogo montado siempre y escondido con `open={false}`, cerrarlo en el paso
 * 2 y volver a abrirlo dejaría el código de lote ya tipeado — o sea, la
 * confirmación quedaría pasada de antemano, que es justo lo que este diálogo
 * existe para impedir. Desmontar tira el estado sin un `useEffect` de reseteo.
 */
export function RecallDialog({ batch, onClose }: RecallDialogProps): ReactNode {
  const { t, fmt } = useI18n();
  const api = useApi();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>('scope');
  const [reason, setReason] = useState('');
  const [typedLot, setTypedLot] = useState('');
  const [touched, setTouched] = useState(false);

  const recall = useMutation<Batch, unknown, string>({
    mutationFn: (motivo) =>
      api.post<Batch>(`/api/v1/batches/${String(batch.id)}/recall`, { reason: motivo }),
    onSuccess: (updated) => {
      setStep('done');
      queryClient.setQueryData(batchKeys.detail(batch.id), updated);
      void queryClient.invalidateQueries({ queryKey: batchKeys.all });
    },
  });

  const reasonError =
    touched && reason.trim().length < MIN_REASON
      ? t('batches.recall.reason.tooShort', { min: MIN_REASON })
      : undefined;
  const lotMatches = typedLot.trim() === batch.lot_code;

  return (
    <Dialog
      open
      onClose={onClose}
      tone={step === 'done' ? 'neutral' : 'critical'}
      title={step === 'done' ? t('batches.recall.done.title') : t('batches.recall.title')}
      description={
        step === 'done'
          ? undefined
          : step === 'scope'
            ? t('batches.recall.step1.description')
            : t('batches.recall.step2.description')
      }
      footer={
        step === 'done' ? (
          <Button variant="primary" onClick={onClose}>
            {t('batches.recall.done.close')}
          </Button>
        ) : step === 'scope' ? (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t('form.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setTouched(true);
                if (reason.trim().length >= MIN_REASON) setStep('confirm');
              }}
            >
              {t('batches.recall.continue')}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setStep('scope');
              }}
              disabled={recall.isPending}
            >
              {t('batches.recall.back')}
            </Button>
            <Button
              variant="critical"
              disabled={!lotMatches || recall.isPending}
              onClick={() => {
                recall.mutate(reason.trim());
              }}
            >
              {recall.isPending ? t('batches.recall.recalling') : t('batches.recall.confirm')}
            </Button>
          </>
        )
      }
    >
      {step === 'scope' && (
        <div className="space-y-4">
          <section aria-labelledby="recall-scope">
            <h3
              id="recall-scope"
              className="text-xs font-semibold tracking-wide text-ink-muted uppercase"
            >
              {t('batches.recall.scope.title')}
            </h3>
            <div className="mt-2 rounded-(--radius-card) border border-border bg-surface-sunken p-4">
              <DetailList>
                <DetailRow
                  label={t('batches.column.product')}
                  value={batch.product_sku ?? `#${String(batch.product_id)}`}
                />
                <DetailRow
                  label={t('batches.column.lot')}
                  value={<code className="font-mono font-semibold">{batch.lot_code}</code>}
                />
                <DetailRow
                  label={t('batches.column.expiry')}
                  value={fmt.expiry(batch.expiry_date)}
                />
                <DetailRow
                  label={t('batches.column.territory')}
                  value={batch.territory ?? t('batches.territory.none')}
                />
                <DetailRow
                  label={t('batches.recall.scope.units')}
                  value={
                    <span className="text-base font-semibold text-critical">
                      {fmt.number(batch.activated_count)}
                    </span>
                  }
                />
                <DetailRow
                  label={t('batches.column.status')}
                  value={<Badge tone="verified">{batch.status}</Badge>}
                />
              </DetailList>
            </div>
          </section>

          <section aria-labelledby="recall-effects">
            <h3
              id="recall-effects"
              className="text-xs font-semibold tracking-wide text-ink-muted uppercase"
            >
              {t('batches.recall.effects.title')}
            </h3>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-ink">
              <li>{t('batches.recall.effects.consumer')}</li>
              <li>{t('batches.recall.effects.alerts')}</li>
              <li className="font-medium text-critical">
                {t('batches.recall.effects.irreversible')}
              </li>
            </ul>
          </section>

          <TextAreaField
            label={t('batches.recall.reason')}
            hint={t('batches.recall.reason.hint')}
            value={reason}
            onChange={setReason}
            error={reasonError}
            required
            rows={3}
            maxLength={500}
          />
        </div>
      )}

      {step === 'confirm' && (
        <div className="space-y-4">
          <p className="text-sm text-ink">
            {t('batches.recall.confirm.summary', {
              lot: batch.lot_code,
              product: batch.product_sku ?? `#${String(batch.product_id)}`,
              units: fmt.number(batch.activated_count),
            })}
          </p>

          <TextField
            label={t('batches.recall.typeLot', { lot: batch.lot_code })}
            hint={t('batches.recall.typeLot.hint')}
            value={typedLot}
            onChange={setTypedLot}
            error={
              typedLot.length > 0 && !lotMatches ? t('batches.recall.typeLot.mismatch') : undefined
            }
            required
            mono
            disabled={recall.isPending}
          />

          {Boolean(recall.error) && <ErrorState error={recall.error} />}
        </div>
      )}

      {step === 'done' && (
        <div className="space-y-3">
          <p className="text-sm text-ink">
            {t('batches.recall.done.detail', { lot: batch.lot_code })}
          </p>
          <ul className="list-disc space-y-1 pl-5 text-sm text-ink-muted">
            <li>{t('batches.recall.done.consumer')}</li>
            <li>{t('batches.recall.done.audit')}</li>
          </ul>
        </div>
      )}
    </Dialog>
  );
}
